import { useEffect, useState } from "react";
import { Control, Controller, useWatch } from 'react-hook-form';

import { useGetStateLookups } from "@/api/use-states";
import { useZipCodeLookup } from "@/api/use-zip-code-lookup";
import { Button } from "@/components-new/button";
import { Input } from "@/components-new/input";
import { UseFormSetValue } from 'react-hook-form';

type Props = {
  zipCode?: string;
  city?: string;
  stateId?: number | undefined;
  control: Control<UserSettingsAddressForm>;
  setValue: UseFormSetValue<UserSettingsAddressForm>;
};

export type UserSettingsAddressForm = {
  zipCode: string;
  city: string;
  stateId: number | undefined;
};

export const UserAddressField = (props: Props) => {
  const [currentZipCode, setCurrentZipCode] = useState(props.zipCode ?? "");
  const [currentCity, setCurrentCity] = useState(props.city ?? "");
  const [currentStateId, setCurrentStateId] = useState(props.stateId ?? undefined);

  const control = props.control;

  // Sync local state with props when they change (after cache invalidation/refetch)
  useEffect(() => {
    setCurrentZipCode(props.zipCode ?? "");
    setCurrentCity(props.city ?? "");
    setCurrentStateId(props.stateId ?? undefined);
  }, [props.zipCode, props.city, props.stateId]);

  const { data: stateLookups } = useGetStateLookups();

  const watchedZipCode = useWatch({ control, name: "zipCode" });
  const watchedCity = useWatch({ control, name: "city" });
  const watchedStateId = useWatch({ control, name: "stateId" });

  const { data: zipLookup, isLoading: isLookingUp } =
    useZipCodeLookup(watchedZipCode);

  // Determine if the suggestion has already been applied
  const suggestionAlreadyApplied =
    zipLookup &&
    zipLookup.city === watchedCity &&
    stateLookups?.find((s) => s.code === zipLookup.stateCode)?.id ===
    watchedStateId;

  const handleSelectSuggestion = () => {
    if (!zipLookup) return;
    const stateId = stateLookups?.find(
      (s) => s.code === zipLookup.stateCode,
    )?.id;
    if (stateId) {
      props.setValue("city", zipLookup.city, { shouldValidate: true });
      props.setValue("stateId", stateId, { shouldValidate: true });
    }
  };

  const getStateName = (stateId: number | null | undefined): string => {
    if (!stateId || !stateLookups) return "";
    const state = stateLookups.find((s) => s.id === stateId);
    return state ? state.label : "";
  };

  const getStateCode = (stateId: number | null | undefined): string => {
    if (!stateId || !stateLookups) return "";
    const state = stateLookups.find((s) => s.id === stateId);
    return state ? state.code : "";
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="zipCode"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Zip Code <span className="text-red-500">*</span>
          </label>
          <Controller
            name="zipCode"
            control={control}
            defaultValue={currentZipCode}
            rules={{
              required: "Zip code is required.",
              minLength: { value: 5, message: "Zip code must be 5 digits." },
              maxLength: { value: 5, message: "Zip code must be 5 digits." },
              pattern: {
                value: /^\d{5}$/,
                message: "Zip code must be 5 digits.",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input
                  {...field}
                  id="zipCode"
                  placeholder="Enter 5-digit zip code"
                  maxLength={5}
                />
                {error && (
                  <p className="mt-1 text-sm text-red-600">{error.message}</p>
                )}
              </div>
            )}
          />
          {isLookingUp && (
            <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Looking up zip code...
            </div>
          )}
          {zipLookup && !suggestionAlreadyApplied && (
            <div className="mt-2">
              <Button
                onClick={handleSelectSuggestion}
                type="button"
                color="secondary"
                className="flex items-center gap-2 text-sm"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                {zipLookup.city}, {zipLookup.stateCode}
                <svg
                  className="w-4 h-4 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </Button>
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="city"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            City <span className="text-red-500">*</span>
          </label>
          <Controller
            name="city"
            control={control}
            defaultValue={currentCity}
            rules={{
              required: "City is required.",
              maxLength: {
                value: 100,
                message: "City must be less than 100 characters.",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input {...field} id="city" disabled className="bg-gray-50" />
                {error && (
                  <p className="mt-1 text-sm text-red-600">{error.message}</p>
                )}
              </div>
            )}
          />
        </div>

        <div>
          <label
            htmlFor="state"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            State <span className="text-red-500">*</span>
          </label>
          <Controller
            name="stateId"
            control={control}
            defaultValue={currentStateId}
            rules={{
              required: "State is required.",
            }}
            render={({ field, fieldState: { error } }) => (
              <div>
                <Input
                  value={getStateName(field.value)}
                  id="state"
                  disabled
                  className="bg-gray-50"
                  onChange={() => {}}
                />
                {error && (
                  <p className="mt-1 text-sm text-red-600">{error.message}</p>
                )}
              </div>
            )}
          />
        </div>

        <p className="text-sm text-gray-500">
          Enter a zip code to automatically populate city and state
        </p>
      </div>
    </div>
  );
};
